import { FC, ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useLatest } from '@srm/ui/hooks/useLatest';
import './Folders.scss';
import { ITreeOption } from '@/types';
// eslint-disable-next-line no-restricted-imports -- активный пункт меню следует за адресом; исполнители действий берут navigate и location роутера
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { DocumentActionsService } from '@services/actions/DocumentActionsService';
import { LocationTypes } from '@models/actions/LocationTypes';
import { IBaseAction } from '@models/actions/IBaseAction';
import { getActionExecutor } from '@organisms/actions/actionExecutorRegistry';
import PlanningMenuActionsControl from '@controls/PlanningMenuActionsControl';
import { FoldersService } from '@services/FoldersService';
import { useAnalytics } from '@/analytics';
import { FolderCountBadge } from '@srm/ui/folder-count-badge';
import { useFolderCounts } from '@hooks/useFolderCounts';
import TourLauncher from '@molecules/Tour/TourLauncher';
import { IAppShellMenuItem, SidebarMenu, useAppShell, useSidebarHold } from '@srm/ui/shell/AppShell';
import {
    collectUnloadedNodesForNode,
    executorModalElement,
    findNodePathByUrl,
    getAdminRootNode,
    getNodeKeys,
    hasFolderCounter,
    lazyLoadItems,
    menuLocationPath,
    NodeKey,
    parseSubMenuFromUrl,
    replaceNodeChildren,
    replaceNodesChildren,
    shouldLazyLoadNode,
    withPreparedRootIcon,
} from './Folders.utils';
import { canPinMenuNode, withDefaultMenuGroups } from './menuNavigation';
import { useMenuIndex } from './useMenuIndex';
import CreateDocumentLauncher from './CreateDocumentLauncher';

export interface IFoldersProps {
    /** Полное меню видно; без значения — из оболочки */
    opened?: boolean;
    /** Показать полное меню; без значения — из оболочки */
    onRequestOpen?: () => void;
    /** Переход из меню; без значения — из оболочки */
    onNavigate?: () => void;
}

const sameItems = (a: IAppShellMenuItem[] | undefined, b: IAppShellMenuItem[] | undefined) =>
    a === b || (!!a && !!b && a.length === b.length && a.every((item, index) => item === b[index]));

/**
 * Пункты меню оболочки из дерева разделов: ключ узла — тот же, что у загрузки веток и активного пункта. Пункт
 * неизменённого узла (тот же объект, ключ и дети) берётся из `cache` прежним объектом: догрузка ветки не
 * перерисовывает остальные пункты. Счётчики — отдельно (`badges` меню).
 */
const toMenuItems = (
    nodes: ITreeOption[],
    cache: WeakMap<ITreeOption, IAppShellMenuItem>,
    byId: Map<NodeKey, ITreeOption>,
    parentKey = '',
): IAppShellMenuItem[] => {
    const keys = getNodeKeys(nodes, parentKey);
    return nodes.map((node, index) => {
        const id = keys[index];
        byId.set(id, node);
        const children = node.children && toMenuItems(node.children, cache, byId, id);
        const cached = cache.get(node);
        if (cached?.id === id && sameItems(cached.children, children)) return cached;
        const item: IAppShellMenuItem = {
            id,
            label: node.label,
            icon: node.icon,
            href: node.url || undefined,
            children,
            hasChildren: node.hasChildren,
            group: node.groupId,
            placement: node.placement,
            pinnable: canPinMenuNode(node),
        };
        cache.set(node, item);
        return item;
    });
};

/** Меню разделов Front: дерево с сервера с догрузкой веток и счётчиками, «Создать документ», действия планирования,
 *  туры. Вид и механика меню — `SidebarMenu` из `@srm/ui/shell/AppShell`. */
const Folders: FC<IFoldersProps> = ({ opened: isOpenProp, onRequestOpen, onNavigate }: IFoldersProps) => {
    const { analytics } = useAnalytics();
    const shell = useAppShell();
    const isOpen = isOpenProp ?? shell.sidebarOpen;

    const user = useTypedSelector((state) => state.auth.user);
    const isAuth = useTypedSelector((state) => state.auth.isAuth);

    const [menuBarActions, setMenuBarActions] = useState<IBaseAction[]>([]);
    const [modalElement, setModalElement] = useState<ReactElement>();

    const [list, setList] = useState<ITreeOption[]>([]);
    const [tourOpen, setTourOpen] = useState(false);
    const menuIndex = useMenuIndex(list, setList, isAuth);
    // меню «Создать документ» держит панель само (CreateDocumentLauncher)
    useSidebarHold(tourOpen || Boolean(modalElement));

    const autoExpandAttempted = useRef<Set<NodeKey>>(new Set());

    // Lazy-счётчики для пунктов меню (opt-in через XML counter="true").
    // Если в дереве нет ни одного counter'а — хук делает ничего.
    const { counts: folderCounts, loading: folderCountsLoading } = useFolderCounts(list);

    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const searchType = searchParams.get('searchType');
    const locationPath = useMemo(
        () => menuLocationPath(location.pathname, location.search),
        [location.pathname, location.search],
    );
    const activePath = useMemo(() => findNodePathByUrl(list, locationPath), [list, locationPath]);

    const [menuItemsCache] = useState(() => new WeakMap<ITreeOption, IAppShellMenuItem>());
    const { items, nodesById, countedNodes } = useMemo(() => {
        const byId = new Map<NodeKey, ITreeOption>();
        const menuItems = toMenuItems(list, menuItemsCache, byId);
        const counted = [...byId].filter(
            (entry): entry is [NodeKey, ITreeOption & { countKey: string }] => hasFolderCounter(entry[1]),
        );
        return { items: menuItems, nodesById: byId, countedNodes: counted };
    }, [list, menuItemsCache]);
    const badges = useMemo(
        () =>
            new Map<NodeKey, ReactNode>(
                countedNodes.map(([id, node]) => [
                    id,
                    <FolderCountBadge
                        count={folderCounts.get(node.countKey)}
                        loading={folderCountsLoading.has(node.countKey)}
                        max={node.counterMax}
                    />,
                ]),
            ),
        [countedNodes, folderCounts, folderCountsLoading],
    );

    const loadChildren = useCallback(
        async (item: IAppShellMenuItem) => {
            const node = nodesById.get(item.id);
            if (!node) return;
            const children = await lazyLoadItems(node);
            setList((prev) => replaceNodeChildren(prev, item.id, children));
        },
        [nodesById],
    );

    useEffect(() => {
        let cancelled = false;

        FoldersService.root().then((res) => {
            if (cancelled) {
                return;
            }

            // Only server sections decide whether legacy grouping applies; the client node has its own group.
            const preparedRoot = withDefaultMenuGroups(res.data.map(withPreparedRootIcon));
            setList(user.canView.adminPanel ? [...preparedRoot, getAdminRootNode()] : preparedRoot);
        });

        return () => {
            cancelled = true;
        };
    }, [user.canView.adminPanel]);

    useEffect(() => {
        DocumentActionsService.fetchActionsByLocation(undefined, LocationTypes.PlanningMenuBar, searchType).then(
            (res) => {
                setMenuBarActions(res.data);
            },
        );
    }, [searchType]);

    useEffect(() => {
        autoExpandAttempted.current = new Set();
    }, [locationPath]);

    // Прямая ссылка в ещё не загруженную ветку: грузим раздел адреса, чтобы найти активный пункт.
    useEffect(() => {
        if (!isAuth || activePath || list.length === 0) {
            return;
        }

        const submenu = parseSubMenuFromUrl(locationPath);
        if (!submenu) {
            return;
        }

        const submenuLower = submenu.toLowerCase();
        const rootIndex = list.findIndex((item) => {
            const value = String(item.value ?? '').toLowerCase();
            if (!value) {
                return false;
            }
            // Значения root-узлов могут быть либо "category/submenu" (например, "Documents/documents"),
            // либо одиночным идентификатором (например, "administrations").
            const parts = value.split('/');
            const submenuPart = parts.length > 1 ? parts[1] : value;
            return submenuPart === submenuLower;
        });
        if (rootIndex < 0) {
            return;
        }

        const rootNode = list[rootIndex];
        // Ключ корня — из ключей ВСЕГО уровня (getNodeKeys зависит от состава уровня),
        // и дальше он же служит parentKey для потомков: так ключи совпадают с теми,
        // по которым рендерится дерево и работает replaceNodeChildren.
        const rootKey = getNodeKeys(list)[rootIndex];

        let cancelled = false;

        const loadRoot = async () => {
            if (autoExpandAttempted.current.has(rootKey)) {
                return false;
            }
            autoExpandAttempted.current.add(rootKey);

            const children = await lazyLoadItems(rootNode);
            if (cancelled) {
                return false;
            }
            setList((prev) => replaceNodeChildren(prev, rootKey, children));
            return true;
        };

        const loadDescendants = async () => {
            const candidates = collectUnloadedNodesForNode(rootNode, rootKey).filter(
                ({ key }) => !autoExpandAttempted.current.has(key),
            );

            if (candidates.length === 0) {
                return;
            }

            candidates.forEach(({ key }) => autoExpandAttempted.current.add(key));

            const results = await Promise.allSettled(candidates.map(({ node }) => lazyLoadItems(node)));
            if (cancelled) {
                return;
            }

            const loaded = new Map<NodeKey, ITreeOption[]>();
            candidates.forEach(({ key }, index) => {
                const result = results[index];
                if (result.status === 'fulfilled') loaded.set(key, result.value);
            });
            setList((prev) => replaceNodesChildren(prev, loaded));
        };

        (async () => {
            if (shouldLazyLoadNode(rootNode)) {
                await loadRoot();
                return;
            }
            await loadDescendants();
        })();

        return () => {
            cancelled = true;
        };
    }, [activePath, isAuth, list, locationPath]);

    // Адрес и переход — на момент щелчка: обработчик постоянный, и слот «Создать документ» не ломает memo меню.
    const router = useLatest({ navigate, location });
    const onCreateDocActionClick = useCallback(
        (act: IBaseAction) => {
            const executor = getActionExecutor('doc', act);
            if (!executor) {
                throw new Error(`Не обнаружен executor для действия ${act.type}`);
            }

            executor.navigate = router.current.navigate;
            executor.location = router.current.location;
            executor.run(
                '',
                undefined,
                act,
                undefined,
                () => {
                    setModalElement(undefined);
                },
                () => {
                    setModalElement(executorModalElement(executor.visualElement));
                },
            );
            setModalElement(executorModalElement(executor.visualElement));

            analytics.trackEvent({
                category: 'Document',
                action: `Клик по кнопке '${act.displayName}' из меню Создать документ на боковой панели`,
                name: `sidebar-create-doc-menu--${act.key}`,
            });
        },
        [analytics],
    );

    const top = useMemo(
        () => (
            <>
                {modalElement}
                <CreateDocumentLauncher searchType={searchType} onAction={onCreateDocActionClick} />
            </>
        ),
        [modalElement, searchType, onCreateDocActionClick],
    );
    const beforeItems = useMemo(
        () => <PlanningMenuActionsControl actions={menuBarActions} collapsed={!isOpen} />,
        [menuBarActions, isOpen],
    );
    const bottom = useMemo(() => <TourLauncher isMenuOpen={isOpen} onOpenChange={setTourOpen} />, [isOpen]);

    if (!isAuth) {
        return <></>;
    }

    return (
        <SidebarMenu
            items={items}
            badges={badges}
            activeId={activePath?.[activePath.length - 1]}
            loadChildren={loadChildren}
            opened={isOpenProp}
            onRequestOpen={onRequestOpen}
            onNavigate={onNavigate}
            pinsScope={user.id}
            pinsLabel="Закреплённые папки"
            searchLoading={menuIndex.loading}
            searchFailed={menuIndex.failed}
            onSearchRetry={menuIndex.retry}
            top={top}
            beforeItems={beforeItems}
            bottom={bottom}
        />
    );
};

export default Folders;
